import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Save, Upload, Target, Eye, Plus, Trash2 } from 'lucide-react';
import { assetUrl, aboutExtendedApi } from '../../../services/api';
import type { AboutSlide } from '../types';
import ContentEditable from '../Shared/ContentEditable';

type AboutEditorProps = {
  slides: AboutSlide[];
  onEdit: (slide: AboutSlide) => void;
  onSave: (id: number) => void | Promise<void>;
  onCreate: () => void;
  onToggleActive: (id: number, currentStatus: boolean) => void | Promise<void>;
  onDelete: (id: number) => void | Promise<void>;
  showInactive: boolean;
  editingAboutId: number | null;
  editingAboutData: Partial<AboutSlide>;
  setEditingAboutData: React.Dispatch<React.SetStateAction<Partial<AboutSlide>>>;
  setEditingAboutId: React.Dispatch<React.SetStateAction<number | null>>;
  modelSource: 'default' | 'custom';
  setModelSource: React.Dispatch<React.SetStateAction<'default' | 'custom'>>;
  sectionHeading: string;
  sectionDescription: string;
  sectionCtaHeading: string;
  sectionCtaDescription: string;
  sectionButtonText: string;
  sectionButtonAction: 'modal' | 'link';
  sectionButtonLink: string;
  onSectionHeadingChange: (value: string) => void;
  onSectionDescriptionChange: (value: string) => void;
  onSectionCtaHeadingChange: (value: string) => void;
  onSectionCtaDescriptionChange: (value: string) => void;
  onSectionButtonTextChange: (value: string) => void;
  onSectionButtonActionChange: (value: 'modal' | 'link') => void;
  onSectionButtonLinkChange: (value: string) => void;
  onSaveSectionContent: () => void | Promise<void>;
  headingLimit: number;
  descriptionLimit: number;
  history: { heading: string; description: string; at: string }[];
  onRestoreVersion: (entry: { heading: string; description: string }) => void;
  onUploadImage: (id?: number) => void | Promise<void>;
};

const AboutEditor: React.FC<AboutEditorProps> = ({
  slides,
  onEdit,
  onSave,
  onCreate,
  onToggleActive,
  onDelete,
  editingAboutId,
  editingAboutData,
  setEditingAboutData,
  sectionHeading,
  sectionDescription,
  sectionCtaHeading,
  sectionCtaDescription,
  sectionButtonText,
  sectionButtonAction,
  sectionButtonLink,
  onSectionHeadingChange,
  onSectionDescriptionChange,
  onSectionCtaHeadingChange,
  onSectionCtaDescriptionChange,
  onSectionButtonTextChange,
  onSectionButtonActionChange,
  onSectionButtonLinkChange,
  onSaveSectionContent,
  headingLimit,
  descriptionLimit,
  history,
  onRestoreVersion,
  onUploadImage,
}) => {
  const [missionVision, setMissionVision] = useState({
    mission_title: '',
    mission_description: '',
    vision_title: '',
    vision_description: '',
  });
  const [coreValues, setCoreValues] = useState<any[]>([]);
  const [whyChoose, setWhyChoose] = useState<any[]>([]);
  const [loadingExtended, setLoadingExtended] = useState(false);

  const primarySlide = slides.find((s) => s.is_active !== false) || slides[0] || null;

  const normalizedFeatures = Array.isArray(editingAboutData.features)
    ? editingAboutData.features
    : Array.isArray(primarySlide?.features) && editingAboutId !== -1
      ? primarySlide.features
      : [];
  const features = normalizedFeatures.slice(0, 6);

  useEffect(() => {
    if (primarySlide && editingAboutId == null) {
      onEdit(primarySlide);
    }
  }, [primarySlide?.id, editingAboutId]);

  // Load extended About data
  useEffect(() => {
    const loadExtendedData = async () => {
      setLoadingExtended(true);
      try {
        const [mvRes, cvRes, wcRes] = await Promise.all([
          aboutExtendedApi.getMissionVision(),
          aboutExtendedApi.getCoreValues(),
          aboutExtendedApi.getWhyChoose(),
        ]);

        if (mvRes?.success && mvRes.data) {
          setMissionVision(mvRes.data);
        }
        if (cvRes?.success && cvRes.data?.values) {
          setCoreValues(cvRes.data.values);
        }
        if (wcRes?.success && wcRes.data?.items) {
          setWhyChoose(wcRes.data.items);
        }
      } catch (error) {
        console.error('Failed to load extended About data:', error);
      } finally {
        setLoadingExtended(false);
      }
    };
    loadExtendedData();
  }, []);

  const handleCreateIfMissing = () => {
    if (!primarySlide && editingAboutId == null) {
      onCreate();
    }
  };

  const saveTargetId = editingAboutId ?? primarySlide?.id ?? -1;
  const imagePreview = editingAboutData.image_url || primarySlide?.image_url || null;
  const resolvedImagePreview =
    typeof imagePreview === 'string' && imagePreview.startsWith('blob:')
      ? imagePreview
      : imagePreview
        ? assetUrl(imagePreview)
        : null;
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="pb-20 space-y-8"
    >
      <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">About Section Intro</h3>
          <button
            onClick={onSaveSectionContent}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm flex items-center gap-2"
          >
            <Save size={14} />
            Save Intro
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Eyebrow Text</label>
            <input
              type="text"
              value={sectionHeading}
              onChange={(e) => onSectionHeadingChange(e.target.value)}
              maxLength={headingLimit}
              className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
            />
            <div className="text-xs text-gray-500 mt-1">{sectionHeading.length}/{headingLimit}</div>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Fallback Intro Text</label>
            <ContentEditable
              html={sectionDescription}
              onChange={onSectionDescriptionChange}
              className="min-h-[120px] w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white prose prose-invert"
            />
            <div className="text-xs text-gray-500 mt-1">{sectionDescription.replace(/<[^>]+>/g, '').length}/{descriptionLimit}</div>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-gray-400 mb-1">CTA Heading</label>
            <input
              type="text"
              value={sectionCtaHeading}
              onChange={(e) => onSectionCtaHeadingChange(e.target.value)}
              className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
              placeholder="Let's Build Something Great"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">CTA Button Text</label>
            <input
              type="text"
              value={sectionButtonText}
              onChange={(e) => onSectionButtonTextChange(e.target.value)}
              className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
              placeholder="Start a Project"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-400 mb-1">CTA Paragraph</label>
            <ContentEditable
              html={sectionCtaDescription}
              onChange={onSectionCtaDescriptionChange}
              className="min-h-[120px] w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white prose prose-invert"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">CTA Button Action</label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-white">
                <input
                  type="radio"
                  name="about_cta_action"
                  checked={sectionButtonAction === 'modal'}
                  onChange={() => onSectionButtonActionChange('modal')}
                />
                Contact Form
              </label>
              <label className="flex items-center gap-2 text-sm text-white">
                <input
                  type="radio"
                  name="about_cta_action"
                  checked={sectionButtonAction === 'link'}
                  onChange={() => onSectionButtonActionChange('link')}
                />
                Custom Link
              </label>
            </div>
          </div>
          {sectionButtonAction === 'link' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Custom Link</label>
              <input
                type="text"
                value={sectionButtonLink}
                onChange={(e) => onSectionButtonLinkChange(e.target.value)}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="https://example.com"
              />
            </div>
          )}
        </div>
        {history.length > 0 && (
          <div className="mt-4 flex items-center gap-2">
            <span className="text-xs text-gray-500">History:</span>
            <select
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white"
              onChange={(e) => {
                const idx = Number(e.target.value);
                const entry = history[idx];
                if (entry) onRestoreVersion(entry);
              }}
            >
              {history.map((h, i) => (
                <option key={i} value={i}>{new Date(h.at).toLocaleString()}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h3 className="text-xl font-bold">About Sections</h3>
          <button
            type="button"
            onClick={onCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
          >
            Add Section
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {slides.map((slide) => {
            const isSelected = (editingAboutId ?? primarySlide?.id) === slide.id;
            return (
              <div
                key={slide.id}
                className={`rounded-xl border p-4 ${isSelected ? 'border-orange-400 bg-orange-500/10' : 'border-white/10 bg-white/[0.03]'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="text-base font-semibold text-white">{slide.title || 'Untitled Section'}</h4>
                    <p className="mt-1 text-xs uppercase tracking-[0.3em] text-white/45">{slide.subtitle || sectionHeading}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.2em] ${slide.is_active !== false ? 'bg-green-500/15 text-green-300' : 'bg-white/10 text-white/45'}`}>
                    {slide.is_active !== false ? 'Active' : 'Hidden'}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit(slide)}
                    className="px-3 py-2 bg-white/10 hover:bg-white/15 rounded-lg text-xs"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(slide.id)}
                    className="px-3 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-200 rounded-lg text-xs"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">About Content</h3>
          <div className="flex items-center gap-2">
            {!primarySlide && editingAboutId == null && (
              <button
                onClick={handleCreateIfMissing}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
              >
                Create About
              </button>
            )}
            <button
              onClick={() => onSave(saveTargetId)}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm flex items-center gap-2"
            >
              <Save size={14} />
              Save Details
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Main Heading</label>
              <input
                type="text"
                value={editingAboutData.title || ''}
                onChange={(e) => setEditingAboutData((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="We Build Digital Products That Scale"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Eyebrow Override</label>
              <input
                type="text"
                value={editingAboutData.subtitle || ''}
                onChange={(e) => setEditingAboutData((prev) => ({ ...prev, subtitle: e.target.value }))}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="WHO WE ARE"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Intro Content</label>
              <ContentEditable
                html={editingAboutData.description || ''}
                onChange={(html) => setEditingAboutData((prev) => ({ ...prev, description: html }))}
                className="min-h-[180px] w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white prose prose-invert"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onUploadImage(saveTargetId > 0 ? saveTargetId : undefined)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-sm flex items-center gap-2"
            >
              <Upload size={14} />
              Upload Image
            </button>
            </div>
            <div className="rounded-xl overflow-hidden border border-white/10 bg-black/30 min-h-[220px] flex items-center justify-center">
              {resolvedImagePreview ? (
                <img src={resolvedImagePreview} alt="About preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white/40 text-sm">No image selected</span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-semibold">Feature Cards</h4>
            <button
              type="button"
              onClick={() =>
                setEditingAboutData((prev) => ({
                  ...prev,
                  features: [
                    ...(Array.isArray(prev.features) ? prev.features : features),
                    {
                      title: '',
                      description: '',
                      icon: 'Box',
                      icon_type: 'library',
                      display_order: features.length,
                    },
                  ],
                }))
              }
              className="px-3 py-2 bg-white/10 hover:bg-white/15 rounded-lg text-sm"
            >
              Add Feature
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div key={index} className="rounded-xl border border-white/10 bg-[#111320] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white/80">Card {index + 1}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setEditingAboutData((prev) => ({
                        ...prev,
                        features: features.filter((_, featureIndex) => featureIndex !== index),
                      }))
                    }
                    className="text-xs text-red-300 hover:text-red-200"
                  >
                    Remove
                  </button>
                </div>
                <input
                  type="text"
                  value={feature.title || ''}
                  onChange={(e) =>
                    setEditingAboutData((prev) => ({
                      ...prev,
                      features: features.map((item, featureIndex) =>
                        featureIndex === index
                          ? {
                              ...item,
                              title: e.target.value,
                              icon: item.icon || 'Box',
                              icon_type: item.icon_type || 'library',
                              display_order: featureIndex,
                            }
                          : item
                      ),
                    }))
                  }
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                  placeholder="Feature title"
                />
                <textarea
                  value={feature.description || ''}
                  onChange={(e) =>
                    setEditingAboutData((prev) => ({
                      ...prev,
                      features: features.map((item, featureIndex) =>
                        featureIndex === index
                          ? {
                              ...item,
                              description: e.target.value,
                              icon: item.icon || 'Box',
                              icon_type: item.icon_type || 'library',
                              display_order: featureIndex,
                            }
                          : item
                      ),
                    }))
                  }
                  rows={3}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white resize-none"
                  placeholder="Feature description"
                />
              </div>
            ))}
            {features.length === 0 && (
              <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-sm text-white/45">
                No feature cards added yet.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mission & Vision Section */}
      <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">Mission & Vision</h3>
          <button
            onClick={async () => {
              await aboutExtendedApi.updateMissionVision(missionVision);
              alert('Mission & Vision saved successfully!');
            }}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm flex items-center gap-2"
          >
            <Save size={14} />
            Save Mission & Vision
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-amber-400">Mission</h4>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Mission Title</label>
              <input
                type="text"
                value={missionVision.mission_title}
                onChange={(e) => setMissionVision({ ...missionVision, mission_title: e.target.value })}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="Our Mission"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Mission Description</label>
              <textarea
                value={missionVision.mission_description}
                onChange={(e) => setMissionVision({ ...missionVision, mission_description: e.target.value })}
                rows={4}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white resize-none"
                placeholder="To empower businesses with innovative technology solutions."
              />
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-amber-400">Vision</h4>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Vision Title</label>
              <input
                type="text"
                value={missionVision.vision_title}
                onChange={(e) => setMissionVision({ ...missionVision, vision_title: e.target.value })}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="Our Vision"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Vision Description</label>
              <textarea
                value={missionVision.vision_description}
                onChange={(e) => setMissionVision({ ...missionVision, vision_description: e.target.value })}
                rows={4}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white resize-none"
                placeholder="To be the global leader in digital transformation."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Core Values Section */}
      <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">Core Values</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCoreValues([...coreValues, { title: '', description: '', icon: 'Target' }])}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm flex items-center gap-2"
            >
              <Plus size={14} />
              Add Value
            </button>
            <button
              onClick={async () => {
                await aboutExtendedApi.updateCoreValues({ values: coreValues });
                alert('Core Values saved successfully!');
              }}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm flex items-center gap-2"
            >
              <Save size={14} />
              Save Core Values
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {coreValues.map((value, index) => (
            <div key={index} className="rounded-xl border border-white/10 bg-[#111320] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/80">Value {index + 1}</span>
                <button
                  onClick={() => setCoreValues(coreValues.filter((_, i) => i !== index))}
                  className="text-xs text-red-300 hover:text-red-200 flex items-center gap-1"
                >
                  <Trash2 size={12} />
                  Remove
                </button>
              </div>
              <input
                type="text"
                value={value.title}
                onChange={(e) => {
                  const newValues = [...coreValues];
                  newValues[index] = { ...newValues[index], title: e.target.value };
                  setCoreValues(newValues);
                }}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="Value title"
              />
              <textarea
                value={value.description}
                onChange={(e) => {
                  const newValues = [...coreValues];
                  newValues[index] = { ...newValues[index], description: e.target.value };
                  setCoreValues(newValues);
                }}
                rows={3}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white resize-none"
                placeholder="Value description"
              />
              <input
                type="text"
                value={value.icon || 'Target'}
                onChange={(e) => {
                  const newValues = [...coreValues];
                  newValues[index] = { ...newValues[index], icon: e.target.value };
                  setCoreValues(newValues);
                }}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="Icon name (e.g., Target, Heart, Shield)"
              />
            </div>
          ))}
          {coreValues.length === 0 && (
            <div className="col-span-2 rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-sm text-white/45">
              No core values added yet. Click "Add Value" to add one.
            </div>
          )}
        </div>
      </div>

      {/* Why Choose Section */}
      <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">Why Choose SOLTECHY Technologies?</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setWhyChoose([...whyChoose, { title: '', description: '', icon: 'Award' }])}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm flex items-center gap-2"
            >
              <Plus size={14} />
              Add Feature
            </button>
            <button
              onClick={async () => {
                await aboutExtendedApi.updateWhyChoose({ items: whyChoose });
                alert('Why Choose features saved successfully!');
              }}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm flex items-center gap-2"
            >
              <Save size={14} />
              Save Features
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {whyChoose.map((item, index) => (
            <div key={index} className="rounded-xl border border-white/10 bg-[#111320] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/80">Feature {index + 1}</span>
                <button
                  onClick={() => setWhyChoose(whyChoose.filter((_, i) => i !== index))}
                  className="text-xs text-red-300 hover:text-red-200 flex items-center gap-1"
                >
                  <Trash2 size={12} />
                  Remove
                </button>
              </div>
              <input
                type="text"
                value={item.title}
                onChange={(e) => {
                  const newItems = [...whyChoose];
                  newItems[index] = { ...newItems[index], title: e.target.value };
                  setWhyChoose(newItems);
                }}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="Feature title"
              />
              <textarea
                value={item.description}
                onChange={(e) => {
                  const newItems = [...whyChoose];
                  newItems[index] = { ...newItems[index], description: e.target.value };
                  setWhyChoose(newItems);
                }}
                rows={3}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white resize-none"
                placeholder="Feature description"
              />
              <input
                type="text"
                value={item.icon || 'Award'}
                onChange={(e) => {
                  const newItems = [...whyChoose];
                  newItems[index] = { ...newItems[index], icon: e.target.value };
                  setWhyChoose(newItems);
                }}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="Icon name (e.g., Award, TrendingUp, Users)"
              />
            </div>
          ))}
          {whyChoose.length === 0 && (
            <div className="col-span-3 rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-sm text-white/45">
              No features added yet. Click "Add Feature" to add one.
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AboutEditor;
